import Vue from 'vue'
import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'
import router from '@/router'
import { LOGOUT } from './auth.module.js'
import { eventBus } from '@/main'
import { generateUniqueId } from '@/core/utils'
import diff from 'deep-diff'
import {
  SET_VPN_SERVER_PENDING_STATE,
  SAVE_VPN_SERVER,
  SAVE_REINSTALL_VPN_SERVER_PARAMS
} from './vpn-servers.module'

// action types
export const GET_SERVERS = 'getServers'
export const GET_SERVER = 'getServer'
export const MANAGE_SERVER = 'manageServer'
export const SWITCH_SERVER = 'switchServer'
export const DELETE_SERVER = 'deleteServer'
export const RESET_SERVER = 'resetServer'

export const MANAGE_VPN_SERVER_STATUS = 'manageVpnServerStatus'
export const GET_REINSTALL_VPN_SERVER_PARAMS = 'getReinstallVpnServerParams'
export const GET_VPN_SERVER_PARAMS = 'getVpnServerParams'
export const REINSTALL_VPN_SERVER = 'reinstallVpnServer'

export const GET_OPENSSH_KEY = 'getOpenSshKey'

export const GET_SERVER_TOKEN = 'getServerToken'
export const REGENERATE_SERVER_TOKEN = 'regenerateServerToken'

export const GET_CLOUDFLARE = 'getCloudlfare'
export const SWITCH_CLOUDFLARE = 'switchCloudlfare'
export const SAVE_CLOUDFLARE_RULES = 'savecloudflareRules'
export const DELETE_SERVER_CLOUFLARE_RULE = 'deletecloudflareRule'

export const SWITCH_SERVER_VISIBILITY = 'switchServerVisibility'

// mutation types
export const SAVE_SERVERS = 'saveServers'
export const SAVE_SERVER = 'saveServer'
export const SET_SERVERS_LIMIT = 'setServersLimit'
export const SET_SERVERS_PAGE = 'setServersPage'
export const SET_SERVERS_GET_PARAM = 'setServersGetParam'
export const SET_SERVERS_GET_PARAMS = 'setServersGetParams'
export const CLEAR_SERVERS_GET_PARAMS = 'clearServersGetParams'
export const CLEAR_SERVERS = 'clearServers'
export const SELECT_SERVER = 'selectServer'
export const UNSELECT_SERVER = 'unselectServer'
export const SAVE_MANAGE_SERVER_ERRORS = 'saveManageServerErrors'
export const CLEAR_MANAGE_SERVER_ERRORS = 'clearManageServerErrors'
export const SET_FAILED_SERVER_GET = 'setFailedDataGet'

export const SAVE_OPENSSH_KEY = 'saveOpenSshKey'
export const CLEAR_OPENSSH_KEY = 'clearOpenSshKey'

export const SAVE_SERVER_TOKEN = 'saveServerToken'
export const CLEAR_SERVER_TOKEN = 'clearServerToken'

export const SAVE_CLOUDFLARE = 'saveCloudlfare'
export const CLEAR_CLOUDFLARE = 'clearCloudlfare'
export const SET_CLOUDFLARE_PENDING_STATUS = 'setcloudflarePendingStatus'
export const ADD_CLOUDFLARE_FIELD = 'addCloudlfareField'
export const REMOVE_CLOUDFLARE_FIELD = 'removeCloudlfareField'
export const SET_CLOUDFLARE_ERROR = 'setCloudlfareError'
export const CLEAR_CLOUDFLARE_ERROR = 'clearCloudlfareError'
export const CLEAR_CLOUDFLARE_ERRORS = 'clearCloudlfareErrors'
export const SAVE_CLOUDFLARE_SNAPSHOT = 'saveCloudflareSnapshot'
export const CLEAR_CLOUDFLARE_SNAPSHOT = 'clearCloudflareSnapshot'

// default constants
const SET_RESPONSE_STATE = 'setResponseState'
export const TOAST_SERVERS = 'showToast/Servers'
export const TOAST_SERVERS_VPN = 'showToast/VpnServers'
export const TOAST_SERVERS_CLOUDFLARE = 'showToast/ServersCloudflare'
export const DEFAULT_SELECTED_SERVER = {
  name: '',
  country: null, // requires to show default option in <b-form-select>
  location: '',
  latitude: '',
  longitude: '',
  group_recipe: null,
  group: [],
  check_connection: true,
  visibility: true,
  cloudflare_proxy: false,
  rmq_queue: '',
  client_version: '',
  ip: '',
  ssh_port: 22, // default
}
export const DEFAULT_SERVERS_GET_PARAMS = {
  limit: 10,
  page: 1,
  sort: 'created',
  ord: 'desc',
}
export const DEFAULT_CLOUDFLARE_DATA = {
  installed: undefined,
  isPending: false,
  rules: []
}
export const DEFAULT_CLOUDFLARE_RULE = {
  _id: null,
  isNew: undefined,
  code: '',
  proxy: 'dns', // temporary #HT-2028
  type: 'DoH', // temporary #HT-2028
  port: '',
  value: '',
}

// returns server based on _id
function findServer(state, id) {
  return state.serversList.filter(item => item._id === id)[0]
}

// converts Array of vpn server paramaters into an object for REST payload
function covertServerForBackend(parameters) {
  const result = {}
  for (let i = 0; i < parameters.length; i++) {
    result[parameters[i].name] = parameters[i].code
  }
  return result
}

// makes some tweaks for cloudflare rules Array to send as REST payload
function convertCloudflareForBackend(state) {
  const rules = [ ...state.cloudflareData.rules ]
  rules.forEach(rule => {
    delete rule._id
    delete rule.isNew
  })
  return rules
}

const state = {
  serversGetParams: { ...DEFAULT_SERVERS_GET_PARAMS },
  serversList: [],
  totalServers: 0,
  totalServersPages: 0,
  selectedServer: { ...DEFAULT_SELECTED_SERVER },
  selectedServerId: null,
  FailedDataGetStatus: undefined,
  manageServerErrors: [],
  openSshKey: '',
  serverToken: '',
  cloudflareData: { ...DEFAULT_CLOUDFLARE_DATA },
  cloudflareSnapshot: [],
  savedCloudflareRulesLength: 0, // indicates number of rules on SERVER
  manageCloudflareErrors: [],
  availableProxies: [
    { value: 'dns', name: 'DNS' }
  ],

  responses: {
    serversList: false,
    getServer: false,
    switchServer: false,
    manageServer: false,
    deleteServer: false,
    manageVpnServer: false,
    getVpnServerParams: false,
    getReinstallVpnServerParams: false,
    reinstallVpnServer: false,
    getOpenSshKey: false,
    getServerToken: false,
    regenerateServerToken: false,
    resetServer: false,
    getCloudlfare: false,
    switchCloudlfare: false,
    switchServerVisibility: false,
  },
}

const getters = {
  serversList(state) {
    return state.serversList
  },
  serversListLength(state) {
    return state.serversList.length
  },
  serversEmpty(state) {
    return state.serversList.length == 0
  },
  // At first I made getter 'serversGetParams' which returns whole object,
  // but it didn't worked out very well.
  // We need to separate params from state.serversGetParams
  // because Vue sometimes triggers watch() event twice when on of them changes.
  // This happenes because <b-pagination> and <b-form-select> are synched.
  // In that case Axios could make two GET requests at once which is bad.
  serversLimit(state) {
    return state.serversGetParams.limit
  },
  serversPage(state) {
    return state.serversGetParams.page
  },
  serversGetParams(state) {
    return state.serversGetParams
  },
  totalServers(state) {
    return state.totalServers
  },
  totalServersPages(state) {
    return state.totalServersPages
  },
  selectedServer(state) {
    return state.selectedServer
  },
  selectedServerId(state) {
    return state.selectedServerId
  },
  FailedDataGetStatus(state) {
    return state.FailedDataGetStatus
  },
  selectedServerExist(state) {
    return state.serversList.some(server => server._id == state.selectedServerId)
  },
  serversModalTitle(state) {
    return state.selectedServerId ? 'Edit server' : 'Add new server'
  },
  manageServerErrors(state) {
    return state.manageServerErrors
  },
  manageServerError: state => field => {
    return state.manageServerErrors.filter(err => err.field == field)
  },
  serverStatusBadge: () => server => {
    const badgeOptions = {
      variant: null,
      icon: null,
      text: null,
    }

    badgeOptions.variant = server.active == 'on' ? 'success' : 'danger'
    badgeOptions.icon = server.active == 'on' ? 'check' : 'times'
    badgeOptions.text = server.active == 'on' ? 'Active' : 'Disabled'

    return badgeOptions
  },
  serverVpnServers: state => id => {
    const target = findServer(state, id)
    return target.vpn_servers
  },
  serverVpnServersEmpty: state => id => {
    const target = findServer(state, id)
    return target.vpn_servers.length == 0
  },
  serverGroupsEmpty: state => id => {
    const target = findServer(state, id)
    return target.group.length == 0
  },
  serverRecipeGroupEmpty: state => id => {
    const target = findServer(state, id)
    return target.group_recipe == null || target.group_recipe == undefined
  },
  openSshKey(state) {
    return state.openSshKey
  },
  openSshKeyEmpty(state) {
    return state.openSshKey.length == 0
  },
  serverToken(state) {
    return state.serverToken
  },
  serverTokenEmpty(state) {
    return state.serverToken.length == 0
  },
  serverVpnServersCount(state) {
    if (state.selectedServerId !== null) {
      return state.selectedServer.vpn_servers.length
    } else {
      return 0
    }
  },
  serverHasRmqQueue: state => id => {
    const target = findServer(state, id)
    return target.rmq_queue && target.rmq_queue.length > 0
  },
  cloudflareData(state) {
    return state.cloudflareData
  },
  cloudflareChanged(state) {
    const differences = diff(state.cloudflareSnapshot, state.cloudflareData.rules)
    return (differences && differences.length > 0) || false
  },
  cloudflareInstalled(state) {
    return state.cloudflareData.installed
  },
  cloudflarePending(state) {
    return state.cloudflareData.isPending
  },
  cloudflareRules(state) {
    return state.cloudflareData.rules
  },
  cloudflareRule: state => id => {
    return state.cloudflareData.rules.filter(rule => rule._id === id)[0]
  },
  cloudflareRulesEmpty(state) {
    return state.cloudflareData.rules.length === 0
  },
  newCloudflareRulesLength(state) {
    return state.cloudflareData.rules.filter(rule => rule.isNew).length || 0
  },
  savedCloudflareRulesLength(state) {
    return state.savedCloudflareRulesLength
  },
  availableProxies(state) {
    return state.availableProxies
  },
  manageCloudflareErrors(state) {
    return state.manageCloudflareErrors
  },
  manageCloudflareErrorsLength(state) {
    return state.manageCloudflareErrors.reduce((sum, error) => sum + error.validations.length, 0)
  },
  manageCloudflareErrorsEmpty(state) {
    return state.manageCloudflareErrors.reduce((sum, error) => sum + error.validations.length, 0) === 0
  },
  manageCloudflareError: state => (id, field) => {
    const target = state.manageCloudflareErrors.filter(rule => rule._id === id)[0]
    return target.validations.filter(err => err.field === field)
  },
  serversResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_SERVERS](context) {
    if (JwtService.getToken()) {
      context.commit(SET_RESPONSE_STATE, { type: 'serversList', status: true })
      eventBus.$emit('startProgress')
      ApiService.setHeader()
      ApiService.get(process.env.VUE_APP_SERVERS, context.state.serversGetParams)
        .then(({ data }) => {
          context.commit(SAVE_SERVERS, data)
        })
        .catch(err => {
          eventBus.$emit(TOAST_SERVERS, {
            message: err.message,
            variant: 'danger'
          })
        })
        .finally(() => {
          context.commit(SET_RESPONSE_STATE, { type: 'serversList', status: false })
          eventBus.$emit('endProgress')
        })
    } else {
      context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
    }
  },
  [GET_SERVER](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getServer', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_SERVER}/${id}`)
          .then(({ data }) => {
            context.commit(SET_FAILED_SERVER_GET, false)
            context.commit(SAVE_SERVER, data)
            resolve(data)
          })
          .catch(err => {
            context.commit(SET_FAILED_SERVER_GET, true)
            eventBus.$emit(TOAST_SERVERS_VPN, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_SERVER](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageServer', status: true })
        eventBus.$emit('startProgress')

        let method
        let url

        if (context.getters.selectedServerExist) {
          method = 'put'
          url = `${process.env.VUE_APP_SERVER}/${state.selectedServerId}`
        } else {
          method = 'post'
          url = process.env.VUE_APP_SERVER
        }

        ApiService[method](url, state.selectedServer)
          .then(({ data }) => {
            eventBus.$emit(TOAST_SERVERS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_SERVER_ERRORS, error.errors)
            eventBus.$emit(TOAST_SERVERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_SERVER](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchServer', status: true })
        eventBus.$emit('startProgress')
        const payload = state.selectedServer.active == 'on' ? 'disable' : 'enable'
        ApiService.put(`${process.env.VUE_APP_SERVER}/${state.selectedServer._id}/${payload}`)
          .then(({ data }) => {
            context.dispatch(GET_SERVER, { id })
            eventBus.$emit(TOAST_SERVERS, {
              message: data.message,
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_SERVER](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteServer', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_SERVER}/${state.selectedServerId}`)
          .then(({ data }) => {
            context.dispatch(GET_SERVERS)
            eventBus.$emit(TOAST_SERVERS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_SERVERS, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_VPN_SERVER_STATUS](context, { action, vpn_code }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageVpnServer', status: true })
        eventBus.$emit('startProgress')

        let method = action == 'install' ? 'post' : 'delete'
        let url = `${process.env.VUE_APP_SERVER}/${state.selectedServerId}/vpn/${vpn_code}`

        ApiService[method](url, {})
          .then(({ data }) => {
            context.commit(SET_VPN_SERVER_PENDING_STATE, vpn_code, { root: true })
            eventBus.$emit(TOAST_SERVERS_VPN, {
              message: 'Request succesfully sent, please wait. It usually takes up to 15 minutes.',
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS_VPN, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageVpnServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_REINSTALL_VPN_SERVER_PARAMS](context, { server_id, vpn_code }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getReinstallVpnServerParams', status: true })
        eventBus.$emit('startProgress')
        ApiService.get(`${process.env.VUE_APP_SERVER}/${server_id}/vpn/${vpn_code}/reinstall`)
          .then(({ data }) => {
            context.commit(
              SAVE_REINSTALL_VPN_SERVER_PARAMS,
              { code: vpn_code, params: data },
              { root: true }
            )
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS_VPN, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getReinstallVpnServerParams', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_VPN_SERVER_PARAMS](context, { server_id, vpn_code }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getVpnServerParams', status: true })
        eventBus.$emit('startProgress')
        ApiService.get(`${process.env.VUE_APP_SERVER}/${server_id}/vpn/${vpn_code}`)
          .then(({ data }) => {
            context.commit(SAVE_VPN_SERVER, data, { root: true })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS_VPN, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getVpnServerParams', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [REINSTALL_VPN_SERVER](context, { server_id, vpn_code, payload }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'reinstallVpnServer', status: true })
        eventBus.$emit('startProgress')
        const convertedPayload = covertServerForBackend(payload)
        ApiService.post(
          `${process.env.VUE_APP_SERVER}/${server_id}/vpn/${vpn_code}/reinstall`,
          convertedPayload)
          .then(({ data }) => {
            eventBus.$emit(TOAST_SERVERS_VPN, {
              message: data.message,
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS_VPN, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'reinstallVpnServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_OPENSSH_KEY](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getOpenSshKey', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(process.env.VUE_APP_SSH_KEY)
          .then(({ data }) => {
            context.commit(SAVE_OPENSSH_KEY, data.pubkey)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getOpenSshKey', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_SERVER_TOKEN](context, { server_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getServerToken', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_SERVER}/${server_id}/token`)
          .then(({ data }) => {
            context.commit(SAVE_SERVER_TOKEN, data.token)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getServerToken', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [REGENERATE_SERVER_TOKEN](context, { server_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'regenerateServerToken', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.post(`${process.env.VUE_APP_SERVER}/${server_id}/token`)
          .then(({ data }) => {
            context.commit(SAVE_SERVER_TOKEN, data.token)
            eventBus.$emit(TOAST_SERVERS, {
              message: 'New access token generated',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'regenerateServerToken', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [RESET_SERVER](context, server) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'resetServer', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_SERVER}/${server._id}/data`)
          .then(({ data }) => {
            context.dispatch(GET_SERVER, { id: server._id })
            eventBus.$emit(TOAST_SERVERS, {
              message: `Server «${server.name}» successfully reseted`,
              variant: 'success'
            })
            eventBus.$emit('clearNotificationsInterval')
            eventBus.$emit('restartNotificationsTimer')
            eventBus.$emit('checkNotificationsCount')
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'resetServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_CLOUDFLARE](context, { server_id, save_rules = true }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getCloudlfare', status: true })
        if (!context.getters.cloudflarePending) {
          eventBus.$emit('startProgress')
        }
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_SERVER}/${server_id}/cloudflare_tunnel`)
          .then(({ data }) => {
            if (data.installed !== context.state.cloudflareData.installed) {
              context.commit(SET_CLOUDFLARE_PENDING_STATUS, false)
            }
            context.commit(SAVE_CLOUDFLARE, { data, save_rules })
            context.commit(SAVE_CLOUDFLARE_SNAPSHOT, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_SERVERS_CLOUDFLARE, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getCloudlfare', status: false })
            if (!context.getters.cloudflarePending) {
              eventBus.$emit('endProgress')
            }
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_CLOUDFLARE](context, { action, server_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchCloudlfare', status: true })
        eventBus.$emit('startProgress')

        let method = action == 'install' ? 'post' : 'delete'
        let url = `${process.env.VUE_APP_SERVER}/${server_id}/cloudflare_tunnel`

        ApiService[method](url, [])
          .then(({ data }) => {
            context.commit(SET_CLOUDFLARE_PENDING_STATUS, true)
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS_CLOUDFLARE, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchCloudlfare', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SAVE_CLOUDFLARE_RULES](context, { action, server_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'savecloudflareRules', status: true })
        eventBus.$emit('startProgress')

        let method = action == 'update' ? 'put' : 'post'
        let url = `${process.env.VUE_APP_SERVER}/${server_id}/cloudflare_tunnel/rules`
        let payload = convertCloudflareForBackend(context.state)

        ApiService[method](url, payload)
          .then(({ data }) => {
            resolve(data)
            eventBus.$emit(TOAST_SERVERS_CLOUDFLARE, {
              message: 'Successfully updated',
              variant: 'success'
            })
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS_CLOUDFLARE, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'savecloudflareRules', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_SERVER_VISIBILITY](context, { id, action }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchServerVisibility', status: true })
        eventBus.$emit('startProgress')
        const method = action === 'hide' ? 'delete' : 'post'
        ApiService[method](`${process.env.VUE_APP_SERVER}/${id}/visibility`)
          .then(({ data }) => {
            context.dispatch(GET_SERVER, { id })
            eventBus.$emit(TOAST_SERVERS, {
              message: data.message,
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_SERVERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchServerVisibility', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_SERVERS](state, servers) {
    state.serversList = servers.list
    state.totalServers = servers.total_elements
    state.totalServersPages = servers.total_pages
  },
  [SAVE_SERVER](state, server) {
    const target = findServer(state, server._id)
    if (target) {
      Object.keys(server).forEach(key => {
        Vue.set(target, key, server[key])
      })
    }
  },
  [CLEAR_SERVERS](state) {
    state.serversList = []
  },
  [SET_SERVERS_LIMIT](state, limit) {
    state.serversGetParams.limit = limit
  },
  [SET_SERVERS_PAGE](state, page) {
    state.serversGetParams.page = page
  },
  [SET_SERVERS_GET_PARAM](state, { value, param }) {
    const origType = typeof state.serversGetParams[param]
    
    if (origType !== 'undefined' && origType !== typeof value) {
      switch (origType) {
        case 'number':
          value = Number(value)
          break
        case 'string':
          value = String(value)
          break
      }
    }

    state.serversGetParams[param] = value
  },
  [SET_SERVERS_GET_PARAMS](state, params) {
    const keys = Object.keys(params)
  
    for (const key of keys) {
      const origType = typeof state.serversGetParams[key]
      const newValue = params[key]
  
      if (typeof newValue !== origType) {
        switch (origType) {
          case 'number':
            Vue.set(state.serversGetParams, key, Number(newValue))
            break
          case 'string':
            Vue.set(state.serversGetParams, key, String(newValue))
            break
        }
      } else {
        Vue.set(state.serversGetParams, key, newValue)
      }
    }
  },
  [CLEAR_SERVERS_GET_PARAMS](state) {
    state.serversGetParams = { ...DEFAULT_SERVERS_GET_PARAMS }
  },
  [SELECT_SERVER](state, group) {
    Object.keys(group).forEach(key => {
      state.selectedServer[key] = group[key]
    })
    // modify .group_recipe to fit v-model of <b-form-radio-group>
    if (state.selectedServer.group_recipe) {
      state.selectedServer.group_recipe = group.group_recipe._id
    }
    // modify .group to fit v-model of <b-form-checkbox-group>
    if (group.group) {
      let arr = []
      for (let n = 0; n < group.group.length; n++) {
        arr.push(group.group[n]._id)
      }
      state.selectedServer.group = arr
    }
    state.selectedServerId = group._id
  },
  [UNSELECT_SERVER](state) {
    state.selectedServer = { ...DEFAULT_SELECTED_SERVER }
    state.selectedServerId = null
  },
  [SAVE_MANAGE_SERVER_ERRORS](state, errors) {
    state.manageServerErrors = errors
  },
  [CLEAR_MANAGE_SERVER_ERRORS](state) {
    state.manageServerErrors = []
  },
  [SAVE_OPENSSH_KEY](state, key) {
    state.openSshKey = key
  },
  [CLEAR_OPENSSH_KEY](state) {
    state.openSshKey = ''
  },
  [SAVE_SERVER_TOKEN](state, key) {
    state.serverToken = key
  },
  [CLEAR_SERVER_TOKEN](state) {
    state.serverToken = ''
  },
  [SET_FAILED_SERVER_GET](state, value) {
    state.FailedDataGetStatus = value
  },
  [SAVE_CLOUDFLARE](state, { data, save_rules }) {
    state.cloudflareData.installed = data.installed
    state.cloudflareData.isPending = false
    state.savedCloudflareRulesLength = data.rules.length
    if (save_rules && data.rules && data.rules.length > 0) {
      state.cloudflareData.rules = data.rules
      data.rules.forEach(rule => {
        state.manageCloudflareErrors.push({
          _id: rule._id,
          validations: [],
        })
      })
    }
  },
  [CLEAR_CLOUDFLARE](state) {
    state.cloudflareData = { ...DEFAULT_CLOUDFLARE_DATA }
  },
  [SET_CLOUDFLARE_PENDING_STATUS](state, status) {
    Vue.set(state.cloudflareData, 'isPending', status)
  },
  [ADD_CLOUDFLARE_FIELD](state) {
    const record = { ...DEFAULT_CLOUDFLARE_RULE }
    const newId = generateUniqueId()
    record._id = newId
    record.isNew = true
    state.cloudflareData.rules.push(record)
    state.manageCloudflareErrors.push({
      _id: newId,
      validations: [],
    })
  },
  [REMOVE_CLOUDFLARE_FIELD](state, id) {
    state.cloudflareData.rules = state.cloudflareData.rules.filter(i => i._id !== id)
    state.manageCloudflareErrors = state.manageCloudflareErrors.filter(i => i._id !== id)
  },
  [SET_CLOUDFLARE_ERROR](state, { id, field, code, message }) {
    const target = state.manageCloudflareErrors.find(rule => rule._id === id)
    if (target && !target.validations.some(err => err.code === code)) {
      target.validations.push({ field, code, message })
    }
  },
  [CLEAR_CLOUDFLARE_ERROR](state, { id, field }) {
    const target = state.manageCloudflareErrors.find(rule => rule._id === id)
    if (target) {
      const index = target.validations.findIndex(err => err.field === field)
      if (index !== -1) {
        target.validations.splice(index, 1)
      }
    }
  },
  [CLEAR_CLOUDFLARE_ERRORS](state) {
    state.manageCloudflareErrors.forEach(err => {
      err.validations = []
    })
  },
  [SAVE_CLOUDFLARE_SNAPSHOT](state, data) {
    if (!data.rules || data.rules.length === 0) {
      return
    }
    for (let i = 0; i < data.rules.length; i++) {
      state.cloudflareSnapshot[i] = Object.assign({}, data.rules[i])
    }
  },
  [CLEAR_CLOUDFLARE_SNAPSHOT](state) {
    state.cloudflareSnapshot = []
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
